/**
 * Global Feedback Service
 * 
 * Centralized service for handling all feedback submissions across the application.
 * Supports underwriting feedback, issue reports, and other feedback types.
 */

import { localConfig } from '../config/localConfig';
import { attachSession, isValidSessionError, handleSessionExpired } from './sessionManager';

/**
 * Submit feedback to backend
 * @param {Object} payload - Feedback payload matching backend schema
 * @returns {Promise<Object>} Response from API
 */
export const submitFeedback = async (payload) => {
  try {
    const apiUrl = `${localConfig.apiBaseUrl}/feedback`;
    
    console.log('[FEEDBACK_SERVICE] Submitting feedback:', payload);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(attachSession(payload))
    });
    
    if (!response.ok) {
      if (await isValidSessionError(response)) {
        handleSessionExpired();
        throw new Error('Session expired');
      }
      throw new Error(`Feedback submission failed: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('[FEEDBACK_SERVICE] Submission successful:', result);
    
    return result;
  } catch (error) {
    console.error('[FEEDBACK_SERVICE] Submission error:', error);
    throw error;
  }
};

/**
 * Format underwriting feedback for API
 * @param {Object} params - Feedback parameters
 * @param {string} params.leadId - Lead ID
 * @param {string} params.userId - User email ID
 * @param {string} params.jobId - Job ID from workflow
 * @param {string} params.doctype - Document type (e.g., 'underwriting_feedback')
 * @param {Object} params.feedback - Feedback object { sectionId: value }
 * @param {Object} params.feedbackConfig - Config sections for labels
 * @param {string} params.comments - User comments
 * @param {string} params.source - Source of feedback (default: 'WEB')
 * @returns {Object} Formatted payload for backend
 */
export const formatUnderwritingFeedback = ({ 
  leadId, 
  userId, 
  jobId, 
  doctype = 'underwriting_feedback',
  feedback, 
  feedbackConfig,
  comments,
  source = 'WEB'
}) => {
  // Transform feedback object to array format
  const feedbackArray = [];
  
  // Add each section's feedback
  Object.entries(feedback).forEach(([sectionId, feedbackData]) => {
    // Handle both old format (direct value) and new format (object with metadata)
    const value = typeof feedbackData === 'object' && feedbackData.value !== undefined
      ? feedbackData.value
      : feedbackData;
    
    const sectionLabel = typeof feedbackData === 'object' && feedbackData.section
      ? feedbackData.section
      : sectionId;
    
    // Skip null/undefined values
    if (value === null || value === undefined) {
      return;
    }
    
    // Try to find section config for additional metadata (backward compatibility)
    const sectionConfig = feedbackConfig.find(s => s.id === sectionId);
    
    if (sectionConfig && sectionConfig.feedback) {
      // Use config if available (old structure)
      feedbackArray.push({
        q_label: sectionConfig.feedback.question || sectionLabel,
        type: sectionConfig.feedback.type === 'thumbs' ? 'boolean' : 'multiple_choice',
        value: value,
        options: sectionConfig.feedback.options?.map(opt => opt.label) || []
      });
    } else {
      // Use metadata from feedback object (new structure)
      feedbackArray.push({
        q_label: sectionLabel,
        type: 'boolean',
        value: value,
        options: []
      });
    }
  });
  
  // Add comments as text feedback
  if (comments && comments.trim()) {
    feedbackArray.push({
      q_label: 'Underwriter Comments',
      type: 'text',
      value: comments.trim(),
      // options: []
    });
  }
  
  return {
    job_id: jobId,
    user_id: userId,
    lead_id: leadId,
    doctype: doctype,
    source: source,
    user_feedback: feedbackArray
  };
};

/**
 * Format issue report feedback for API
 * @param {Object} params - Issue report parameters
 * @param {string} params.userId - User email ID
 * @param {string} params.issueText - Issue description
 * @param {string} params.doctype - Document type (category-based)
 * @param {Object} params.context - Additional context (leadId, jobId, category, page)
 * @param {string} params.source - Source of feedback (default: 'WEB')
 * @returns {Object} Formatted payload for backend
 */
export const formatIssueFeedback = ({ 
  userId, 
  issueText,
  doctype = 'ISSUE_REPORT',
  context = {},
  source = 'WEB'
}) => {
  const timestamp = new Date().toISOString();
  const jobId = context.jobId || `issue_${userId.toLowerCase()}_${Date.now()}`;
  
  const feedbackArray = [];
  
  // Add category if provided
  if (context.category) {
    feedbackArray.push({
      q_label: 'Category',
      type: 'text',
      value: context.category,
      options: []
    });
  }
  
  // Add issue description
  feedbackArray.push({
    q_label: 'Issue Description',
    type: 'text',
    value: issueText,
    options: []
  });
  
  // Add page/document context if available
  if (context.page) {
    feedbackArray.push({
      q_label: 'Document/Page',
      type: 'text',
      value: context.page,
      options: []
    });
  }
  
  return {
    job_id: jobId,
    user_id: userId,
    lead_id: context.leadId || 'N/A',
    doctype: doctype,
    source: source,
    user_feedback: feedbackArray
  };
};
